import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { resellerBranding } from '@/api/resellerBranding';
import type { ResellerBrandingRow } from '@/api/resellerBranding';

const DEFAULT_BRAND: ResellerBrandingRow = {
  reseller_id: '',
  brand_name: 'Ivrello',
  logo_url: null,
  favicon_url: null,
  primary_color: null,
  secondary_color: null,
  email_from_name: null,
  email_from_addr: null,
  sms_sender_name: null,
  kb_welcome_md: null,
  updated_at: new Date().toISOString(),
};

/**
 * Fetches the current user's reseller branding and keeps it in query cache.
 * Falls back to Ivrello defaults when no row exists (super-admin, or a
 * reseller that hasn't set up branding yet).
 *
 * Also updates the <title> and favicon live.
 */
export function useBranding(): ResellerBrandingRow {
  const { data } = useQuery({
    queryKey: ['reseller-branding'],
    queryFn: () => resellerBranding.get(),
    staleTime: 60_000,
  });

  const brand = data?.branding ?? DEFAULT_BRAND;

  useEffect(() => {
    const titleBase = brand.brand_name || DEFAULT_BRAND.brand_name;
    document.title = document.title.includes(' · ')
      ? `${document.title.split(' · ')[0]} · ${titleBase}`
      : titleBase;

    if (brand.favicon_url) {
      let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = brand.favicon_url;
    }
  }, [brand.brand_name, brand.favicon_url]);

  return brand;
}
